import { SITE_CONFIG } from '../config';
import { assistantIdentity } from '../services/assistantConversation';
import React, { useEffect, useRef } from 'react';
import PrivacyChoicesLink from './PrivacyChoicesLink';

const ChatComposer = ({
  user,
  openChat,
  hidePrompts,
  showPrompts,
  onTogglePrompts,
  prompts,
  onPromptSelect,
  inputValue,
  onInputChange,
  onKeyPress,
  onSend,
  onSignIn,
  conversationFirst = false,
  introductory = false,
  voiceControls,
  tools,
  isBusy = false
}) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 132)}px`;
  }, [inputValue]);

  const handleComposerKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent?.isComposing && !event.isComposing && event.keyCode !== 229) {
      event.preventDefault();
      if (!isBusy && inputValue.trim()) onKeyPress(event);
    }
  };

  const suggestedQuestions = (!hidePrompts && prompts.length > 0 && (!conversationFirst || introductory) && (
            <div className="assistant-suggestion-row" aria-label="Suggested questions">
              {conversationFirst && <span className="assistant-starter-label">Try a question</span>}
              {prompts.slice(0, 4).map((prompt) => (
                <button
                  key={prompt.value}
                  type="button"
                  className="assistant-suggestion-chip"
                  disabled={isBusy}
                  onClick={() => { onPromptSelect(prompt.value); if (conversationFirst) textareaRef.current?.focus({preventScroll: true}); }}
                >
                  {prompt.display}
                </button>
              ))}
              {!conversationFirst && <button
                type="button"
                className="assistant-more-prompts"
                onClick={onTogglePrompts}
                aria-expanded={showPrompts}
              >
                More
              </button>}
              {!conversationFirst && showPrompts && (
                <div className="prompts-menu assistant-prompts-menu">
                  {prompts.map((prompt) => (
                    <button
                      type="button"
                      key={prompt.value}
                      className="prompt-item"
                      disabled={isBusy}
                      onClick={() => onPromptSelect(prompt.value)}
                    >
                      {prompt.display}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ));

  return (
    <div className="chat-input-container" role={openChat || user ? 'search' : undefined}>
      {!openChat && !user ? (
        <button type="button" className="chat-sign-in-prompt" onClick={onSignIn}>
          <span>Continue with {assistantIdentity(SITE_CONFIG).label}</span>
          <small>Sign in or create an account to start a conversation.</small>
        </button>
      ) : (
        <div className={`assistant-composer-shell ${conversationFirst ? 'assistant-chat-composer' : ''}`}>
          {introductory && <p className="assistant-chat-instruction">Type a message below, or use the microphone to talk.</p>}
          {!conversationFirst && suggestedQuestions}
          <div className="assistant-composer-field">
            <textarea
              ref={textareaRef}
              rows="1"
              placeholder={conversationFirst ? "Type your message here…" : "Ask about homes, alerts, selling, or scheduling a showing…"}
              className="chat-input"
              value={inputValue}
              onChange={(event) => onInputChange(event.target.value)}
              onKeyDown={handleComposerKeyDown}
              aria-label="Chat message input - Ask about properties, schedule showings, or get real estate assistance"
            />
            <button
              type="button"
              className="send-button"
              onClick={onSend}
              aria-label="Send chat message to AI assistant"
              disabled={isBusy || !inputValue.trim()}
            >
              {conversationFirst ? 'Send' : <span aria-hidden="true">↑</span>}
            </button>
          </div>
          {voiceControls && <div className="assistant-composer-voice">{voiceControls}</div>}
          {conversationFirst && suggestedQuestions}
          {tools && <details className="assistant-chat-tools"><summary>Saved homes &amp; more</summary>{tools}</details>}
          <div className="assistant-composer-meta">
            <span>Enter to send · Shift + Enter for a new line</span>
            <span>Private conversation with {SITE_CONFIG.ASSISTANT?.BRAND_NAME || SITE_CONFIG.AGENT.BROKERAGE || SITE_CONFIG.AGENT.NAME}</span>
            <PrivacyChoicesLink />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatComposer;
